import {
  type ViewLeases,
  ViewEstablishments,
  AnalysisType,
  CarbonFootprintAnalysisView,
  type UserPopulate,
  type SerializedCustomer,
  type SerializedEstablishment,
  type SerializedFile,
  type SerializedMeter,
  type SerializedSupplyPoint,
  type CostAnalysisKind,
  type ConsumptionAnalysisKind,
  type ViewSupplyPoints,
  type SerializedLeaseUnit,
  type SerializedLease,
  type SerializedFloorPlan,
  type SerializedTenant,
  type SerializedBillingSetting,
  type SerializedBilling,
  type NotificationKind,
  type SerializedNotification,
  type SerializedNotificationSettings,
  type MeterPopulate,
} from '@enmon/commons/lib/enmon';
import type { FilesFilter } from '@/services/api/files';
import type { Filter } from '@/components/filter/constants';
import type { SupplyPointsBulkAction, SupplyPointsBulkArgs, SupplyPointsFilter } from '@/services/api/supplyPoints';
import type { BackendQuery } from '@/types/BackendQuery';
import type { GetCustomEmissionFactorArg, GetCustomEmissionFactorsArg } from '@/services/api/records/emission_factors';
import type { CustomersFilter } from '@/services/api/customers';
import type { GetExportsArgs, GetOneExportArg, PreviewExportArg, ValidateExportArg } from '@/services/api/exports';
import type {
  GetEmissionFactorsArg,
  GetHardwareItemsArg,
  GetManufacturerArg,
  GetOneManufacturerArg,
  GetSettingsArgs,
  GetThirdPartiesArg,
} from '@/services/api/tools';
import type { MetersFilter } from '@/services/api/meters';
import type { EmissionRecordsFilter } from '@/services/api/records/emissions';
import type { AnalysisFilter } from '@/services/api/analysis';
import type { EstablishmentsFilter, GetCostEfficiencyArg } from '@/services/api/establishments/types';
import type {
  GetEmissionFactorValiditiesArg,
  GetEmissionFactorValidityArg,
} from '@/services/api/records/emission_factor_validity';
import type { GetManualRecordArg, ManualRecordsFilter } from '@/services/api/records/manual';
import type { GetRevisionsArg } from '@/services/api/meters/types/args';
import type { TenantsFilter, TenantStatsFilter } from '@/services/api/tenants';
import type { LeasesFilter } from '@/services/api/leases';
import type { RemoteReadingValuesFilter } from '@/services/api/readings/remote/values';
import type { FloorPlansFilter } from '@/services/api/floor_plans';
import type { GetNotificationsArg } from '@/services/api/notifications/types/args';
import type {
  GetNotificationRulesPreviewArg,
  GetNotificationSettingsArg,
  GetNotificationSettingWatchedArg,
} from '@/services/api/notification_settings/types/args';
import type { Args as UsersArgs } from '@/services/api/users';
import type { Args as FavouritesArgs } from '@/services/api/users/favourites';
import type { SerializedUser } from '@enmon/commons/lib/enmon/users/user';
import type { BillingSettingsFilter } from '@/services/api/billing_settings';
import type { BillingsFilter } from '@/services/api/billings';
import type { GetLeaseUnitsLeasedStateArg } from '@/services/api/lease_units/types/args';
import type { DashboardFilter } from '@/services/api/dashboard';
import type { SettingsType } from '@enmon/commons/lib/shared/settings/settingsType';
import type { ViewManualRecords } from '@enmon/commons/lib/enmon/records/manual/view';
import type { ViewInvoiceRecords } from '@enmon/commons/lib/enmon/records/invoice/view';
import { DEFAULT_USE_METER_POPULATE } from '@/services/hooks/meters/constants';
import type { Commodity } from '@enmon/commons';

export const QUERY_KEYS = {
  customers: {
    all: () => ['customers'] as const,
    me: () => [...QUERY_KEYS.customers.all(), 'me'] as const,
    one: (customerId: SerializedCustomer['_id']) => [...QUERY_KEYS.customers.all(), customerId] as const,
    config: (customerId: SerializedCustomer['_id']) => [...QUERY_KEYS.customers.one(customerId), 'config'] as const,
    stats: (customerId: SerializedCustomer['_id']) => [...QUERY_KEYS.customers.one(customerId), 'stats'] as const,
    filter: (filter: CustomersFilter) => [...QUERY_KEYS.customers.all(), filter] as const,
  },
  dashboard: {
    all: () => ['dashboard'] as const,
    filter: (filter: DashboardFilter) => [...QUERY_KEYS.dashboard.all(), filter] as const,
  },
  tenants: {
    all: () => ['tenants'] as const,
    one: (tenantId: SerializedTenant['_id']) => [...QUERY_KEYS.tenants.all(), tenantId] as const,
    filter: (filter: TenantsFilter) => [...QUERY_KEYS.tenants.all(), filter] as const,
    stats: (filter: TenantStatsFilter) => [...QUERY_KEYS.tenants.all(), 'stats', filter] as const,
  },
  billings: {
    all: () => ['billings'] as const,
    one: (billingId: SerializedBilling['_id']) => [...QUERY_KEYS.billings.all(), billingId] as const,
    onePreview: (billingId: SerializedBilling['_id']) => [...QUERY_KEYS.billings.one(billingId), 'preview'] as const,
    integrity: (billingId: SerializedBilling['_id']) => [...QUERY_KEYS.billings.one(billingId), 'integrity'] as const,
    filter: (filter: BillingsFilter) => [...QUERY_KEYS.billings.all(), filter] as const,
  },
  billingSettings: {
    all: () => ['billing-settings'] as const,
    one: (billingSettingId: SerializedBillingSetting['_id']) =>
      [...QUERY_KEYS.billingSettings.all(), billingSettingId] as const,
    filter: (filter: BillingSettingsFilter) => [...QUERY_KEYS.billingSettings.all(), filter] as const,
  },
  leaseUnits: {
    all: () => ['lease-units'] as const,
    one: (leaseUnitId: SerializedLeaseUnit['_id']) => [...QUERY_KEYS.leaseUnits.all(), leaseUnitId] as const,
    leasedState: (filter: GetLeaseUnitsLeasedStateArg) =>
      [...QUERY_KEYS.leaseUnits.all(), 'leased-state', filter] as const,
    filter: (filter: TenantsFilter) => [...QUERY_KEYS.leaseUnits.all(), filter] as const,
  },
  leases: {
    all: () => ['leases'] as const,
    one: (leaseId: SerializedLease['_id']) => [...QUERY_KEYS.leases.all(), leaseId] as const,
    filter: (filter: LeasesFilter, view: ViewLeases) => [...QUERY_KEYS.leases.all(), filter, view] as const,
  },
  users: {
    all: () => ['users'] as const,
    me: {
      user: () => [...QUERY_KEYS.users.all(), 'me', 'user'] as const,
      consents: () => [...QUERY_KEYS.users.all(), 'me', 'consents'] as const,
    },
    one: ({ userId, filter }: { userId: SerializedUser['_id']; filter?: { populate?: string[] } }) =>
      [...QUERY_KEYS.users.all(), userId, ...(filter ? [filter] : [])] as const,
    filter: <TPopulate extends UserPopulate = UserPopulate.Customer>(filter?: UsersArgs.GetUsersArgs<TPopulate>) =>
      [...QUERY_KEYS.users.all(), ...(filter ? [filter] : [])] as const,
    favourites: {
      all: (userId: FavouritesArgs.GetFavouritesArgs['userId']) =>
        [...QUERY_KEYS.users.all(), userId, 'favourites'] as const,
      one: ({ userId, favouriteId }: FavouritesArgs.GetFavouriteArgs) =>
        [...QUERY_KEYS.users.favourites.all(userId), userId, 'favourites', favouriteId] as const,
    },
    access: {
      all: () => [...QUERY_KEYS.users.all(), 'me', 'access'] as const,
      filter: (filter: UsersArgs.GetMyAccessArgs) => [...QUERY_KEYS.users.access.all(), filter] as const,
    },
  },
  meters: {
    all: () => ['meters'] as const,
    one: (meterId: SerializedMeter['_id'], populate: MeterPopulate[] = DEFAULT_USE_METER_POPULATE) =>
      [...QUERY_KEYS.meters.all(), meterId, ...populate] as const,
    filter: (filter: MetersFilter) => [...QUERY_KEYS.meters.all(), filter] as const,
    stats: (filter: MetersFilter) => [...QUERY_KEYS.meters.all(), 'stats', filter] as const,
    counter: {
      all: () => [...QUERY_KEYS.meters.all(), 'counter'] as const,
      filter: (filter: BackendQuery) => [...QUERY_KEYS.meters.counter.all(), filter] as const,
    },
    availableRegisters: () => [...QUERY_KEYS.meters.all(), 'availableRegisters'] as const,
    registers: {
      all: (meterId: SerializedMeter['_id']) => [...QUERY_KEYS.meters.all(), meterId, 'registers'] as const,
    },
    children: {
      all: (meterId: SerializedMeter['_id']) => [...QUERY_KEYS.meters.all(), meterId, 'children'] as const,
    },
    revisions: {
      all: ({ meterId, ...filter }: GetRevisionsArg) =>
        [...QUERY_KEYS.meters.all(), meterId, 'revisions', filter] as const,
    },
  },
  analysis: {
    all: () => ['analysis'] as const,
    configurator: (configuration: unknown) => [...QUERY_KEYS.analysis.all(), 'configurator', configuration] as const,
    consumption: {
      all: () => [...QUERY_KEYS.analysis.all(), AnalysisType.Consumption] as const,
      kind: (kind: ConsumptionAnalysisKind, filter?: Partial<BackendQuery>) =>
        [...QUERY_KEYS.analysis.all(), AnalysisType.Consumption, kind, ...(filter ? [filter] : [])] as const,
    },
    values: (filter?: Partial<BackendQuery>) =>
      [...QUERY_KEYS.analysis.all(), AnalysisType.Values, ...(filter ? [filter] : [])] as const,
    cost: (kind: CostAnalysisKind, filter?: Partial<BackendQuery>) =>
      [...QUERY_KEYS.analysis.all(), AnalysisType.Cost, kind, ...(filter ? [filter] : [])] as const,
    invoices: {
      all: () => [...QUERY_KEYS.analysis.all(), AnalysisType.Invoices] as const,
      type: (commodity: Commodity | undefined, filter?: Partial<BackendQuery>) =>
        [...QUERY_KEYS.analysis.invoices.all(), commodity, ...(filter ? [filter] : [])] as const,
    },
    carbonFootprint: (
      filter?: AnalysisFilter,
      view: CarbonFootprintAnalysisView = CarbonFootprintAnalysisView.Default,
    ) => [...QUERY_KEYS.analysis.all(), AnalysisType.CarbonFootprint, filter, view] as const,
    chartCategories: (filter?: Partial<BackendQuery>) =>
      [...QUERY_KEYS.analysis.all(), AnalysisType.ChartCategories, ...(filter ? [filter] : [])] as const,
    dataIntegrity: (filter?: Partial<BackendQuery>) =>
      [...QUERY_KEYS.analysis.all(), AnalysisType.DataIntegrity, ...(filter ? [filter] : [])] as const,
  },
  files: {
    all: () => ['files'] as const,
    filter: (filter?: FilesFilter) => [...QUERY_KEYS.files.all(), filter] as const,
    one: (fileId: SerializedFile['_id']) => [...QUERY_KEYS.files.all(), fileId] as const,
    link: (bucketPath?: SerializedFile['bucketPath']) => [...QUERY_KEYS.files.all(), 'link', bucketPath] as const,
    content: (bucketPath: SerializedFile['bucketPath']) => [...QUERY_KEYS.files.all(), 'content', bucketPath] as const,
    contentV2: (fileId: SerializedFile['_id']) => [...QUERY_KEYS.files.all(), fileId, 'content'] as const,
    thumbnail: (fileId: SerializedFile['_id']) => [...QUERY_KEYS.files.all(), fileId, 'thumbnail'] as const,
  },
  establishments: {
    all: () => ['establishments'] as const,
    filter: (filter: EstablishmentsFilter, view: ViewEstablishments = ViewEstablishments.Default) =>
      [...QUERY_KEYS.establishments.all(), filter, view] as const,
    one: (establishmentId: SerializedEstablishment['_id']) =>
      [...QUERY_KEYS.establishments.all(), establishmentId] as const,
    costEfficiency: (establishmentId: SerializedEstablishment['_id'], filter: GetCostEfficiencyArg['filter']) =>
      [...QUERY_KEYS.establishments.one(establishmentId), 'cost-efficiency', filter] as const,
  },
  floorPlans: {
    all: () => ['floor-plans'] as const,
    filter: (filter: FloorPlansFilter) => [...QUERY_KEYS.floorPlans.all(), filter] as const,
    one: (floorPlanId: SerializedFloorPlan['_id']) => [...QUERY_KEYS.floorPlans.all(), floorPlanId] as const,
  },
  gateways: {
    filter: (filter: Record<string, unknown>) => ['gateways', filter] as const,
  },
  records: {
    all: () => ['records'] as const,
    invoice: {
      all: () => [...QUERY_KEYS.records.all(), 'invoices'] as const,
      filter: (filter: Record<string, unknown>, view: ViewInvoiceRecords) =>
        [...QUERY_KEYS.records.invoice.all(), filter, view] as const,
    },
    manual: {
      all: () => [...QUERY_KEYS.records.all(), 'manual'] as const,
      one: (manualRecordId: GetManualRecordArg) => [...QUERY_KEYS.records.manual.all(), manualRecordId] as const,
      filter: (filter: ManualRecordsFilter, view: ViewManualRecords) =>
        [...QUERY_KEYS.records.manual.all(), filter, view] as const,
    },
    emission: {
      all: () => [QUERY_KEYS.records.all(), 'emission'] as const,
      filter: (filter: EmissionRecordsFilter, customer?: SerializedCustomer['_id']) =>
        [...QUERY_KEYS.records.emission.all(), filter, customer] as const,
    },
    emissionFactors: {
      all: () => [...QUERY_KEYS.records.all(), 'emission-factors'] as const,
      filter: (filter: GetCustomEmissionFactorsArg) => [...QUERY_KEYS.records.emissionFactors.all(), filter] as const,
      one: (emissionFactorId: GetCustomEmissionFactorArg) =>
        [...QUERY_KEYS.records.emissionFactors.all(), emissionFactorId] as const,
    },
    emissionFactorValidities: {
      all: () => [...QUERY_KEYS.records.all(), 'emission-factor-validities'] as const,
      filter: (filter: GetEmissionFactorValiditiesArg) =>
        [...QUERY_KEYS.records.emissionFactorValidities.all(), filter] as const,
      one: (arg: GetEmissionFactorValidityArg) =>
        [...QUERY_KEYS.records.emissionFactorValidities.all(), arg.factorId, arg.validityId] as const,
    },
  },
  readings: {
    all: () => ['readings'] as const,
    remote: {
      all: () => [...QUERY_KEYS.readings.all(), 'remote'] as const,
      consumption: {
        all: () => [...QUERY_KEYS.readings.remote.all(), 'consumption'] as const,
        filter: (filter: Record<string, unknown>) => [...QUERY_KEYS.readings.remote.consumption.all(), filter] as const,
      },
      values: {
        all: () => [...QUERY_KEYS.readings.remote.all(), 'values'] as const,
        filter: (filter: RemoteReadingValuesFilter) => [...QUERY_KEYS.readings.remote.values.all(), filter] as const,
      },
    },
  },
  tools: {
    all: () => ['tools'] as const,
    emissionFactorCategories: {
      all: () => [...QUERY_KEYS.tools.all(), 'emission-factor-categories'] as const,
    },
    emissionFactors: {
      all: () => [...QUERY_KEYS.tools.all(), 'emission-factors'] as const,
      filter: (filter: GetEmissionFactorsArg) => [...QUERY_KEYS.tools.emissionFactors.all(), filter] as const,
    },
    thirdParties: (arg: GetThirdPartiesArg) => [...QUERY_KEYS.tools.all(), 'third-parties', arg] as const,
    settings: {
      all: () => ['settings'] as const,
      filter: (type: SettingsType, filter: GetSettingsArgs) =>
        [...QUERY_KEYS.tools.settings.all(), type, filter] as const,
    },
    manufacturers: {
      all: (arg: GetManufacturerArg) => [...QUERY_KEYS.tools.all(), 'manufacturers', arg] as const,
      one: (arg: GetOneManufacturerArg) => [...QUERY_KEYS.tools.manufacturers.all(arg), arg] as const,
    },
    hardwareItems: (arg: GetHardwareItemsArg) => [...QUERY_KEYS.tools.all(), 'hardware-items', arg] as const,
  },
  supplyPoints: {
    all: () => ['supply-points'] as const,
    filter: (filter: SupplyPointsFilter) => [...QUERY_KEYS.supplyPoints.all(), filter] as const,
    withAnalysis: (filter: SupplyPointsFilter) => [...QUERY_KEYS.supplyPoints.all(), 'withAnalysis', filter] as const,
    stats: (filter: SupplyPointsFilter) => [...QUERY_KEYS.supplyPoints.all(), 'stats', filter] as const,
    metadata: (filter: SupplyPointsFilter, view: ViewSupplyPoints) =>
      [...QUERY_KEYS.supplyPoints.all(), 'metadata', filter, view] as const,
    one: (supplyPointId: SerializedSupplyPoint['_id']) => [...QUERY_KEYS.supplyPoints.all(), supplyPointId] as const,
    revisionHistory: (supplyPointId: SerializedSupplyPoint['_id']) =>
      [...QUERY_KEYS.supplyPoints.all(), supplyPointId, 'revision-history'] as const,
    hasHistory: (supplyPointId: SerializedSupplyPoint['_id']) =>
      [...QUERY_KEYS.supplyPoints.all(), supplyPointId, 'has-history'] as const,
    bulkPreview: <TAction extends SupplyPointsBulkAction>(action: TAction, arg: SupplyPointsBulkArgs[TAction] | null) =>
      [...QUERY_KEYS.supplyPoints.all(), 'bulk-preview', action, arg] as const,
  },
  notifications: {
    all: () => ['notifications'] as const,
    unread: (customerId: SerializedCustomer['_id'] | null | undefined) =>
      [...QUERY_KEYS.notifications.all(), 'unread', customerId] as const,
    lists: () => [...QUERY_KEYS.notifications.all(), 'list'] as const,
    list: (filter: GetNotificationsArg, kind: NotificationKind | undefined) =>
      [...QUERY_KEYS.notifications.lists(), kind, filter] as const,
    details: () => [...QUERY_KEYS.notifications.all(), 'detail'] as const,
    detail: (id: SerializedNotification['_id'] | undefined) => [...QUERY_KEYS.notifications.details(), id] as const,
  },
  // apart from `notifications`: status, seen and mute changes must not reload the fixed events of a batch
  notificationBatches: {
    all: () => ['notificationBatches'] as const,
    events: (id: SerializedNotification['_id']) => [...QUERY_KEYS.notificationBatches.all(), id, 'events'] as const,
  },
  notificationSettings: {
    all: () => ['notificationSettings'] as const,
    lists: () => [...QUERY_KEYS.notificationSettings.all(), 'list'] as const,
    list: (filter: GetNotificationSettingsArg) => [...QUERY_KEYS.notificationSettings.lists(), filter] as const,
    mutedByMe: () => [...QUERY_KEYS.notificationSettings.all(), 'mutedByMe'] as const,
    detail: (id: SerializedNotificationSettings['_id']) =>
      [...QUERY_KEYS.notificationSettings.all(), 'detail', id] as const,
    rules: (id: SerializedNotificationSettings['_id'] | undefined) =>
      [...QUERY_KEYS.notificationSettings.all(), 'rules', id] as const,
    watched: (id: SerializedNotificationSettings['_id']) =>
      [...QUERY_KEYS.notificationSettings.all(), 'watched', id] as const,
    watchedPage: (id: SerializedNotificationSettings['_id'], filter: GetNotificationSettingWatchedArg) =>
      [...QUERY_KEYS.notificationSettings.watched(id), filter] as const,
    rulesPreviews: () => [...QUERY_KEYS.notificationSettings.all(), 'rulesPreview'] as const,
    rulesPreview: (arg: GetNotificationRulesPreviewArg | undefined) =>
      [...QUERY_KEYS.notificationSettings.rulesPreviews(), arg] as const,
  },
  exports: {
    all: () => ['exports'] as const,
    getOne: (exportId: GetOneExportArg) => [...QUERY_KEYS.exports.all(), exportId] as const,
    getOneStatus: (exportId: GetOneExportArg) => [...QUERY_KEYS.exports.all(), exportId, 'status'] as const,
    filter: (filter: GetExportsArgs) => [...QUERY_KEYS.exports.all(), filter] as const,
    preview: (data: PreviewExportArg) => [...QUERY_KEYS.exports.all(), 'preview', data] as const,
    validate: (data: ValidateExportArg) => [...QUERY_KEYS.exports.all(), 'validate', data] as const,
  },
  externalData: {
    analysis: (analysisType: AnalysisType, dataTypes: string[], filter?: Filter) =>
      ['external-data', 'analysis', analysisType, dataTypes, filter] as const,
  },
} as const;
